/**
 * Calculates the balance change and its direction based on current balance and 24h rate change
 * @param currentBalanceUsd - Current balance in USD
 * @param change24h - 24h percentage change in rate (e.g. 5.5 for 5.5% increase)
 * @returns Object containing the balance change amount and whether it's positive
 */
export function calculateBalanceChange(
  currentBalanceUsd: number | undefined | null,
  change24h: number | undefined | null
): { balanceChange: number | null; isPositive: boolean } {
  if (!currentBalanceUsd || typeof change24h !== 'number') {
    return { balanceChange: null, isPositive: true };
  }

  // Calculate previous balance using: current = previous * (1 + change%)
  // So: previous = current / (1 + change%)
  const denominator = 1 + change24h / 100;

  // Guard against division by zero or near-zero (≤ -100% change)
  // Also guard against values that would produce Infinity
  if (denominator <= 0 || !isFinite(denominator)) {
    return { balanceChange: null, isPositive: false };
  }

  const previousBalanceUsd = currentBalanceUsd / denominator;
  const balanceChange = currentBalanceUsd - previousBalanceUsd;

  // Guard against non-finite results
  if (!isFinite(balanceChange)) {
    return { balanceChange: null, isPositive: false };
  }

  return {
    balanceChange,
    isPositive: balanceChange >= 0,
  };
}
